import {
  ANNUALIZED_GROWTH_RATE,
  ANNUAL_SHARE,
  CONTRACTION_VELOCITY,
  DURABLE_MRR,
  EXPANSION_SATURATION,
  EXPANSION_VELOCITY,
  GROWTH_ACCELERATION,
  GROWTH_VOLATILITY,
  MONTHS_TO_DOUBLE,
  REACTIVATION_SHARE,
  SMOOTHED_MRR_GROWTH,
} from "./exampleData";
import { MUTED } from "./chartTheme";
import MetricBarChart from "./MetricBarChart";
import AmountBarChart from "./AmountBarChart";

const pct = (v: number) => `${v}%`;
const pts = (v: number) => `${v}pp`;
const mo = (v: number) => `${v} mo`;
const zero = { y: 0 }; // the axis already shows 0 — line only

export const AnnualizedGrowthRateChart = () => (
  <MetricBarChart
    caption="Annualized growth rate runs 50–63% — the latest monthly MRR growth compounded across twelve months. It translates a modest-looking monthly figure into the yearly pace investors judge you on."
    data={ANNUALIZED_GROWTH_RATE}
    seriesName="Annualized growth"
    format={pct}
    domain={[0, "dataMax + 15"]}
  />
);

export const SmoothedMrrGrowthChart = () => (
  <MetricBarChart
    caption="Smoothed MRR growth holds near 4.2–4.8% — a three-month average that strips the noise out of raw monthly growth so you can see the underlying trend rather than the wobble."
    data={SMOOTHED_MRR_GROWTH}
    seriesName="Smoothed growth"
    format={pct}
    domain={[0, "dataMax + 3"]}
  />
);

export const MonthsToDoubleChart = () => (
  <MetricBarChart
    caption="At the current growth rate, MRR doubles in about 17–19 months. It reframes a growth percentage as a timeline — the single most intuitive way to convey how fast the business is compounding."
    data={MONTHS_TO_DOUBLE}
    seriesName="Months to double"
    format={mo}
    domain={[0, "dataMax + 4"]}
    yWidth={52}
  />
);

export const GrowthAccelerationChart = () => (
  <MetricBarChart
    caption="Growth acceleration is the change in the growth rate itself — the second derivative of MRR. Above zero, growth is speeding up; below, it is slowing even if MRR still rises."
    data={GROWTH_ACCELERATION}
    seriesName="Growth acceleration"
    format={pts}
    color={MUTED}
    domain={[-1, 1]}
    reference={zero}
  />
);

export const GrowthVolatilityChart = () => (
  <MetricBarChart
    caption="Growth volatility — the spread of the growth rate across recent months — eases from 1.5 to 0.9 points. Lower volatility means a more predictable business, which markets reward with a premium."
    data={GROWTH_VOLATILITY}
    seriesName="Growth volatility"
    format={pts}
    color={MUTED}
    domain={[0, "dataMax + 1"]}
  />
);

export const ExpansionVelocityChart = () => (
  <MetricBarChart
    caption="Expansion velocity climbs from 22% to 30% — the share of new customers who expand within 60 days. Fast expansion is the clearest sign your product creates reasons to pay more, quickly."
    data={EXPANSION_VELOCITY}
    seriesName="Expansion velocity"
    format={pct}
    domain={[0, "dataMax + 10"]}
  />
);

export const ContractionVelocityChart = () => (
  <MetricBarChart
    caption="Contraction velocity falls from 12% to 8% — the share of new customers who contract or churn within 60 days. A high early figure means you are winning customers who were never a good fit."
    data={CONTRACTION_VELOCITY}
    seriesName="Contraction velocity"
    format={pct}
    color={MUTED}
    domain={[0, "dataMax + 6"]}
  />
);

export const ReactivationShareChart = () => (
  <MetricBarChart
    caption="Reactivation makes up 4–11% of gross MRR adds — how much of your new revenue is really returning customers. A high, rising share can flatter acquisition while masking a churn problem."
    data={REACTIVATION_SHARE}
    seriesName="Reactivation share"
    format={pct}
    color={MUTED}
    domain={[0, "dataMax + 6"]}
  />
);

export const AnnualShareChart = () => (
  <MetricBarChart
    caption="The share of MRR on annual contracts grows from 35% to 48%. Annual revenue is far more durable than month-to-month — it locks in a year of retention and smooths cash flow."
    data={ANNUAL_SHARE}
    seriesName="Annual share"
    format={pct}
    domain={[0, 100]}
  />
);

export const ExpansionSaturationChart = () => (
  <MetricBarChart
    caption="Expansion saturation rises from 28% to 40% — the share of paying customers who have expanded at least once. As it climbs, your remaining expansion runway narrows, which reshapes where growth must come from."
    data={EXPANSION_SATURATION}
    seriesName="Expansion saturation"
    format={pct}
    domain={[0, 100]}
  />
);

export const DurableMrrChart = () => (
  <AmountBarChart
    caption="Durable MRR — the revenue still standing a year from now at the current churn rate — grows from $16,000 to $18,500. It is the conservative floor beneath your headline MRR, before any new business."
    data={DURABLE_MRR}
    seriesName="Durable MRR"
  />
);
